import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { ErrorBox } from "@components/cards/messageBox";
import { NavBar } from "@components/navigation/NavBar/NavBar";
import { IPricingRecord } from "@services/QuoteBuilder/QuoteBuilderService";
import { CustomerPotentialDetail } from "./customerPotentialDetailReducer";
import { QuoteValidationError } from "../../utils/quoteValidation";

type Props = {
  customerPotentialDetailState: CustomerPotentialDetail;
  pricingRecordState: IPricingRecord | null;
  navigateLandingPageTarget: string;
};

const InvalidPlanTypeError = () => {
  return (
    <ErrorBox
      data-testid="invalid-plan-type-error"
      message={
        <>
          A sales quote <strong>can not be sent</strong> for this potential
          using Smart Ops.
          <br />
          <br />
          Smart Ops currently can only be used to generate Flexi Own or Flexi
          Own Pre-Owned sales quotes.
          <br />
          <br />
          Please use the <strong>Send Quote</strong> option on the Zoho
          Potential action menu to issue a quote using the alternative
          workflow.{" "}
        </>
      }
    />
  );
};

const InvalidCustomerStateError = () => {
  return (
    <ErrorBox
      data-testid="invalid-customer-location-error"
      message={
        <>
          A <strong>Quote can not be created</strong> because the contact does
          not have a State recorded.
          <br />
          <br />
          Please update the contact record with a valid State before creating a
          quote.
        </>
      }
    />
  );
};

const InvalidPotentialStageError = () => {
  return (
    <ErrorBox
      data-testid="invalid-potential-stage-error"
      message={
        <>
          A sales quote <strong>can not be sent</strong> for this potential
          using Smart Ops.
          <br />
          <br />
          It is not possible to send a quote to a customer for a Potential that
          is in this stage.
          <br />
          <br />
          Please create a new Potential for this customer to create a quote.
        </>
      }
    />
  );
};

const MissingMinimumPeriodWeekForFTError = () => {
  return (
    <ErrorBox
      data-testid="missing-minimum-period-week-for-ft-error"
      message={
        <>
          A <strong>Quote can not be created</strong> because the minimum period 
          is missing
          <br />
          <br />
          Please return to Potential and select a minimum period before creating 
          a quote for this initiative.
        </>
      }
    />
  );
};

const UnsupportedSplendInitiativeError = () => {
  return (
    <ErrorBox
      data-testid="unsupported-splend-initiative-error"
      message={
        <>
          You must select Dynamic Pricing or Ignition to proceed
          <br />
          <br />
          Please return to the Potential and select the correct Initiative 
          before creating a quote.
        </>
      }
    />
  );
};

export default function QuoteErrorView({
  customerPotentialDetailState,
  pricingRecordState,
  navigateLandingPageTarget,
}: Props) {
  const navigate = useNavigate();

  // Redirect to landing page if there is no error and a pricing record exists
  useEffect(() => {
    if (
      customerPotentialDetailState?.error === null &&
      pricingRecordState === null
    ) {
      navigate(navigateLandingPageTarget);
    }
  }, [
    pricingRecordState,
    customerPotentialDetailState,
    navigateLandingPageTarget,
    navigate,
  ]);

  return (
    <>
      <NavBar pageTitle="Sales Quote" />
      <div style={{ margin: "2.5vh 2.5vw" }}>
        {/* Invalid Potential Stage - Quote generation is not allowed for Closed Potentials */}
        {!pricingRecordState &&
          customerPotentialDetailState?.error ===
            QuoteValidationError.INVALID_POTENTIAL_STAGE && (
            <InvalidPotentialStageError />
          )}

        {/* Invalid Plan Type - Flexi Own and Preowned Flexi Own are the only plan types supported */}
        {!pricingRecordState &&
          customerPotentialDetailState?.error ===
            QuoteValidationError.INVALID_PLAN_TYPE && <InvalidPlanTypeError />}

        {/* Invalid Customer Location - This checks if the Zoho Contact record has a State */}
        {!pricingRecordState &&
          customerPotentialDetailState?.error ===
            QuoteValidationError.INVALID_CUSTOMER_LOCATION && (
            <InvalidCustomerStateError />
          )}

        {/* Missing Minimum Period Week for Flexi Try - This checks if the minimum period is specified in the potential */}
        {!pricingRecordState &&
          customerPotentialDetailState?.error ===
          QuoteValidationError.MISSING_MINIMUM_PERIOD_WEEKS_FOR_FT && (
            <MissingMinimumPeriodWeekForFTError />
          )}

        {/* Unsupported Splend Initiative - This checks if the splend initiative is either Dynamic Pricing or Ignition */}
        {!pricingRecordState &&
          customerPotentialDetailState?.error ===
          QuoteValidationError.UNSUPPORTED_SPLEND_INITIATIVE && (
            <UnsupportedSplendInitiativeError />
          )}
      </div>
    </>
  );
}
